import { FC, useCallback, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  giftChoiceUnitListDataAtom,
  giftChoiceUnitListStatusAtom,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import { deleteGiftChoiceUnit } from '@shared/api/giftChoiceUnit/giftChoiceUnit.controller';
import { useGiftChoiceUnitListHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { Link } from 'react-router-dom';

export const GiftChoiceUnitListPage: FC = function GiftChoiceUnitListPage() {
  const { sellerRole } = useAuth();

  useGiftChoiceUnitListHook(sellerRole());
  useGiftItemBrandListHook(sellerRole());

  const status = useRecoilValue(giftChoiceUnitListStatusAtom);
  const data = useRecoilValue(giftChoiceUnitListDataAtom);
  const setData = useSetRecoilState(giftChoiceUnitListDataAtom);

  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  const [searchQuery, setSearchQuery] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // 필터링된 데이터
  const filteredData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    // 검색 필터
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query),
      );
    }

    // 브랜드 필터
    if (brandFilter !== 'all') {
      if (brandFilter === 'none') {
        result = result.filter((item) => !item.giftItemBrand);
      } else {
        result = result.filter((item) => {
          const brandId =
            typeof item.giftItemBrand === 'object'
              ? item.giftItemBrand?._id
              : item.giftItemBrand;
          return brandId === brandFilter;
        });
      }
    }

    return result;
  }, [data, searchQuery, brandFilter]);

  // 삭제 핸들러
  const handleDelete = useCallback(
    async (unit: GiftChoiceUnitData) => {
      if (!confirm(`"${unit.name}" 선택유닛을 삭제하시겠습니까?`)) return;

      try {
        setDeletingId(unit._id);
        await deleteGiftChoiceUnit(unit._id, sellerRole());
        setData((prev) => prev.filter((item) => item._id !== unit._id));
        alert('삭제되었습니다.');
      } catch (error) {
        console.error(error);
        alert('삭제에 실패했습니다.');
      } finally {
        setDeletingId(null);
      }
    },
    [sellerRole, setData],
  );

  const itemListRender = useMemo(() => {
    if (status === 'success') {
      if (filteredData.length === 0) {
        return (
          <div className="py-8 text-center text-gray-500">
            {searchQuery || brandFilter !== 'all'
              ? '검색 결과가 없습니다.'
              : '등록된 선택유닛이 없습니다.'}
          </div>
        );
      }

      return (
        <div className="space-y-2">
          {filteredData.map((item) => (
            <div
              key={item._id}
              className="flex items-center justify-between rounded-lg border bg-white p-4 hover:bg-gray-50"
            >
              <div className="flex flex-1 items-center gap-3">
                {/* 이미지 */}
                <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                  {item.image && typeof item.image === 'object' ? (
                    <img
                      src={fileBucketUrl(item.image.url, 'w200')}
                      alt={item.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                      No
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {/* 브랜드 뱃지 */}
                    {item.giftItemBrand && typeof item.giftItemBrand === 'object' && (
                      <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-600">
                        {item.giftItemBrand.name}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 font-medium">{item.name}</p>
                </div>
                {/* 가격 표시 */}
                {(item.priceRegular || item.priceWhole) && (
                  <div className="flex flex-1 items-center gap-3 text-sm">
                    <div>
                      {item.priceRegular ? (
                        <div className="font-medium text-gray-700">
                          {item.priceRegular.toLocaleString()}원
                        </div>
                      ) : null}
                      {item.priceWhole ? (
                        <div className="text-xs text-gray-400">
                          (도매 {item.priceWhole.toLocaleString()}원)
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Link
                  to={`/gift-seller/choice-unit/${item._id}/edit`}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
                >
                  수정
                </Link>
                <button
                  onClick={() => handleDelete(item)}
                  disabled={deletingId === item._id}
                  className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {deletingId === item._id ? '삭제 중...' : '삭제'}
                </button>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="center-box h-screen-15">
        <Loading />
      </div>
    );
  }, [status, filteredData, searchQuery, brandFilter, deletingId, handleDelete]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">선택유닛 관리</h1>
          <Link to="/gift-seller/choice-unit/new">
            <Button className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white">
              + 선택유닛 등록
            </Button>
          </Link>
        </div>

        {/* 통계 */}
        {status === 'success' && (
          <div className="mt-3 text-sm text-gray-600">
            전체 <span className="font-medium text-gray-900">{data?.length || 0}</span>개
          </div>
        )}
      </div>

      {/* 필터 및 검색 */}
      <div className="border-b bg-white px-6 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* 검색 */}
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="유닛명, 설명 검색..."
              className="w-full rounded-lg border px-3 py-2 pl-9 text-sm"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </span>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* 브랜드 필터 */}
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="rounded-lg border px-3 py-2 text-sm"
            disabled={brandListStatus !== 'success'}
          >
            <option value="all">전체 브랜드</option>
            <option value="none">브랜드 없음</option>
            {brandList.map((brand) => (
              <option key={brand._id} value={brand._id}>
                {brand.name}
              </option>
            ))}
          </select>
        </div>

        {/* 검색 결과 수 */}
        {status === 'success' && (searchQuery || brandFilter !== 'all') && (
          <div className="mt-2 text-xs text-gray-500">
            {filteredData.length}개 표시 (전체 {data?.length || 0}개 중)
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">{itemListRender}</div>
    </div>
  );
};
